export type TourId = 'conversation' | 'team' | 'reviews' | 'contact' | 'import_contacts' | 'campaign' | 'team_chat';
export interface Scope { businessId: string; userId: string }
export interface TourRecord {
  version: number; status: 'started' | 'dismissed' | 'completed'; runId?: string;
  lastStartedAt?: string; lastDismissedAt?: string; completedAt?: string; completedVersion?: number;
}
export interface WalkthroughResponse {
  audience: Scope & { businessSetup: 'unknown'; memberCohort: 'new' | 'existing'; role: 'admin' | 'team_leader' | 'member' };
  progress: Scope & { schemaVersion: 1; welcomeDismissedAt?: string; tours: Partial<Record<TourId, TourRecord>> };
  availableTourIds: TourId[];
}
export interface ProgressState { data: WalkthroughResponse | null; loading: boolean; busy: boolean; error: string | null }
export type Transport = (method: 'GET' | 'POST', path: string, body?: Record<string, unknown>) => Promise<unknown>;

const tourIds: TourId[] = ['conversation', 'team', 'reviews', 'contact', 'import_contacts', 'campaign', 'team_chat'];
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);

/** Reject another login/business's response before it can affect either learning or prompts. */
export function validateResponse(value: unknown, scope: Scope): WalkthroughResponse {
  if (!object(value) || !object(value.audience) || !object(value.progress)
    || value.audience.businessId !== scope.businessId || value.audience.userId !== scope.userId
    || value.progress.businessId !== scope.businessId || value.progress.userId !== scope.userId
    || value.progress.schemaVersion !== 1 || !object(value.progress.tours)
    || value.audience.businessSetup !== 'unknown'
    || !['new', 'existing'].includes(String(value.audience.memberCohort))
    || !['admin', 'team_leader', 'member'].includes(String(value.audience.role))
    || !Array.isArray(value.availableTourIds) || value.availableTourIds.some(id => !tourIds.includes(id as TourId))) {
    throw new Error('Invalid walkthrough response');
  }
  for (const [id, record] of Object.entries(value.progress.tours)) {
    if (!tourIds.includes(id as TourId) || !object(record) || record.version !== 1
      || !['started', 'dismissed', 'completed'].includes(String(record.status))
      || (record.runId !== undefined && typeof record.runId !== 'string')
      || (record.status === 'completed' && (typeof record.completedAt !== 'string' || record.completedVersion !== 1))) {
      throw new Error('Invalid walkthrough progress');
    }
  }
  return value as unknown as WalkthroughResponse;
}

/** One session per authenticated person/business. No browser persistence or optimistic completion. */
export function createProgressClient(scope: Scope, transport: Transport, changed: (state: ProgressState) => void) {
  let state: ProgressState = { data: null, loading: true, busy: false, error: null };
  let disposed = false;
  let request = 0;
  const emit = (patch: Partial<ProgressState>) => {
    if (disposed) return;
    state = { ...state, ...patch }; changed(state);
  };
  const load = async (): Promise<WalkthroughResponse | null> => {
    if (disposed || state.busy) return null;
    const current = ++request;
    emit({ loading: true, error: null });
    try {
      const data = validateResponse(await transport('GET', '/api/walkthroughs'), scope);
      if (disposed || current !== request) return null;
      emit({ data, loading: false }); return data;
    } catch {
      if (!disposed && current === request) emit({ data: null, loading: false, error: 'We couldn’t load your walkthroughs. Please try again.' });
      return null;
    }
  };
  const mutate = async (path: string, body: Record<string, unknown>): Promise<WalkthroughResponse | null> => {
    if (disposed || state.busy || state.loading || !state.data) return null;
    const current = ++request;
    emit({ busy: true, error: null });
    try {
      const data = validateResponse(await transport('POST', path, body), scope);
      if (disposed || current !== request) return null;
      emit({ data, busy: false }); return data;
    } catch (error) {
      if (disposed || current !== request) return null;
      const conflict = object(error) && object(error.response) && error.response.status === 409;
      emit({ busy: false, error: 'We couldn’t save your progress. Please try again.' });
      if (conflict) {
        await load();
        if (!disposed) emit({ error: 'Your progress changed in another window. Please start the guide again.' });
      }
      return null;
    }
  };
  return {
    activate: () => { disposed = false; },
    isActive: () => !disposed,
    load,
    dismissWelcome: () => mutate('/api/walkthroughs/welcome/dismiss', {}),
    start: (id: TourId, runId: string) => {
      if (!state.data?.availableTourIds.includes(id)) return Promise.resolve(null);
      const expectedRunId = state.data.progress.tours[id]?.runId;
      return mutate(`/api/walkthroughs/${id}/start`, { version: 1, runId, ...(expectedRunId ? { expectedRunId } : {}) });
    },
    finish: (id: TourId, runId: string, action: 'dismiss' | 'complete') => mutate(`/api/walkthroughs/${id}/${action}`, { version: 1, runId }),
    dispose: () => { disposed = true; ++request; },
  };
}
